import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import type { FieldError } from "react-hook-form";

import { Input } from "../../../../../components/ui/uikit/input";
import { cn } from "../../../../../lib/utils";
import type { InsuranceProvider } from "../api/useInsuranceProviders";
import { INSURER_NAME_MAX_LENGTH } from "./InsuranceInformation.schema";

interface InsurerNameFieldProperties {
  readonly label: string;
  readonly value: string;
  readonly providers: InsuranceProvider[];
  readonly onChange: (value: string) => void;
  readonly onBlur: () => void;
  readonly onActivate: () => void;
  readonly onPhoneFocus: () => void;
  readonly disabled: boolean;
  readonly error?: FieldError;
}

export function InsurerNameField({
  label,
  value,
  providers,
  onChange,
  onBlur,
  onActivate,
  onPhoneFocus,
  disabled,
  error,
}: InsurerNameFieldProperties) {
  const id = useId();
  const listId = `${id}-options`;
  const errorId = `${id}-error`;
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const listReference = useRef<HTMLUListElement>(null);
  const matches = providers.filter((provider) => provider.name.toUpperCase().includes(value.toUpperCase()));
  const exactOnly = matches.length === 1 && matches[0].name.toUpperCase() === value.toUpperCase();
  const expanded = open && !disabled && matches.length > 0 && !exactOnly;
  const activeIndex = expanded && highlight < matches.length ? highlight : -1;

  useEffect(() => {
    if (activeIndex >= 0) {
      listReference.current?.children[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  function select(provider: InsuranceProvider) {
    onChange(provider.name.toUpperCase());
    setOpen(false);
    setHighlight(-1);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        setOpen(true);
        setHighlight(
          event.key === "ArrowDown" ? Math.min(activeIndex + 1, matches.length - 1) : Math.max(activeIndex - 1, 0)
        );
        break;
      }
      case "Enter": {
        event.preventDefault();
        if (activeIndex >= 0) {
          select(matches[activeIndex]);
        }
        break;
      }
      case "Escape": {
        event.preventDefault();
        setOpen(false);
        break;
      }
      case "Tab": {
        if (!event.shiftKey) {
          event.preventDefault();
          onPhoneFocus();
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  return (
    <div className="relative flex flex-col gap-1">
      <label htmlFor={id} className={cn("text-xs font-medium", error && "text-destructive")}>
        {label}
      </label>
      <Input
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={expanded ? listId : undefined}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={error ? errorId : undefined}
        autoComplete="off"
        value={value}
        maxLength={INSURER_NAME_MAX_LENGTH}
        disabled={disabled}
        onFocus={() => {
          onActivate();
          setOpen(true);
        }}
        onChange={(event) => {
          onChange(event.target.value.slice(0, INSURER_NAME_MAX_LENGTH));
          setOpen(true);
          setHighlight(-1);
        }}
        onBlur={() => {
          setOpen(false);
          setHighlight(-1);
          onBlur();
        }}
        onKeyDown={handleKeyDown}
      />
      {expanded && (
        <ul
          ref={listReference}
          id={listId}
          role="listbox"
          aria-label={label}
          className="bg-popover border-border absolute top-full z-20 max-h-60 w-full overflow-y-auto rounded-md border shadow-md"
        >
          {matches.map((provider, index) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keyboard selection stays on the combobox input
            <li
              key={`${provider.name}-${index}`}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setHighlight(index)}
              onClick={() => select(provider)}
              className={cn("h-6 cursor-pointer truncate px-2 py-1 text-xs", index === activeIndex && "bg-accent")}
            >
              {provider.name}
            </li>
          ))}
        </ul>
      )}
      {error?.message && (
        <p id={errorId} role="alert" className="text-destructive text-2xs">
          {error.message}
        </p>
      )}
    </div>
  );
}
